import { useState, useEffect, useCallback } from "react";
import {
  Users as UsersIcon,
  Briefcase,
  Search,
  X,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  getEmployees,
  getRegisteredUsers,
} from "@/services/users";

import { getBranchEmployees } from "@/services/branchIncharge";
import {
  getZoneEmployees,
  getZoneRegisteredProfiles,
} from "@/services/zoneManager";

import EmployeeList from "./EmployeeList";
import UserList from "./UserList";

import { getMasterDropdownService } from "@/services/masters";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/store";
import { useRoleContext } from "@/hooks/useRoleContext";

export default function UserManagement() {
  const {
    loaded: permsLoaded,
    data: permissions,
  } = useSelector((state: RootState) => state.permissions);

  const {
    isBranchIncharge,
    isZoneManager,
    branchCode,
    zoneCode,
  } = useRoleContext();

  const canSeeEmployees =
    !permsLoaded ||
    ("employees" in permissions &&
      permissions["employees"]?.Can_View !== false);

  const canSeeUsers =
    !permsLoaded ||
    ("resgistered profile" in permissions &&
      permissions["resgistered profile"]?.Can_View !== false);

 const defaultTab: "employees" | "users" = canSeeEmployees
    ? "employees"
    : canSeeUsers
      ? "users"
      : "employees";

  const [activeTab, setActiveTab] = useState<
    "employees" | "users"
  >(defaultTab);

  const [employees, setEmployees] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("True");

  const [roles, setRoles] = useState<any[]>([]);

  const [empPage, setEmpPage] = useState(1);
  const [empTotalPages, setEmpTotalPages] = useState(1);
  const [empTotalCount, setEmpTotalCount] = useState(0);

  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotalCount, setUsersTotalCount] = useState(0);

  const loadEmployees = useCallback(
    async (
      role: string = "",
      page: number = 1,
      search: string = "",
      status: string = "True"
    ) => {
      setLoading(true);

      try {
        let res;

        if (isZoneManager && zoneCode) {
          res = await getZoneEmployees(
            zoneCode,
            page,
            search || undefined,
            status || "True"
          );
        } else if (isBranchIncharge && branchCode) {
          res = await getBranchEmployees(
            branchCode,
            page,
            search || undefined,
            status || "True"
          );
        } else {
          res = await getEmployees(
            role !== "" ? role : undefined,
            page,
            search || undefined,
            status || "True"
          );
        }

        const d = res?.data as any;

        if (d?.results) {
          setEmployees(d.results);
          setEmpTotalPages(d.total_pages ?? 1);
          setEmpTotalCount(d.count ?? 0);
        } else {
          setEmployees([]);
          setEmpTotalPages(1);
          setEmpTotalCount(0);
        }
      } catch (error) {
        console.error("Error loading employees:", error);
        toast.error("Error loading employees");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadUsers = useCallback(
    async (
      page: number = 1,
      search: string = "",
      status: string = "True"
    ) => {
      if (!canSeeUsers && !isBranchIncharge) {
        return;
      }

      setLoading(true);

      try {
        let response;

        if (isZoneManager && zoneCode) {
          response = await getZoneRegisteredProfiles(
            zoneCode,
            page,
            search || undefined,
            status || "True"
          );
        } else {
          response = await getRegisteredUsers(
            page,
            search || undefined,
            isBranchIncharge && branchCode
              ? branchCode
              : undefined,
            status || "True"
          );
        }

        const d = response?.data as any;

        if (d) {
          setUsers(d.results ?? d ?? []);

          setUsersTotalPages(
            d.total_pages ?? 1
          );

          setUsersTotalCount(
            d.count ??
              (Array.isArray(d)
                ? d.length
                : d.results?.length ?? 0)
          );
        } else {
          setUsers([]);
          setUsersTotalPages(1);
          setUsersTotalCount(0);
        }
      } catch (error) {
        console.error("Error loading users:", error);
        toast.error("Error loading users");
      } finally {
        setLoading(false);
      }
    },
    [
      canSeeUsers,
    ]
  );


  const loadRoles = useCallback(async () => {
    try {
      const response =
        await getMasterDropdownService();

      if (
        response?.status === 200 &&
        response?.data
      ) {
        const data = response.data as any;

        setRoles(
          data.Designations || []
        );
      }
    } catch (error) {
      console.error(
        "Error loading roles:",
        error
      );
    }
  }, []);

   useEffect(() => {
    loadEmployees(roleFilter, 1, "", '');
    loadUsers(1, "", '');
    loadRoles();
  }, []);

  useEffect(() => {
    const loadActiveTab = async () => {
      if (activeTab === "employees") {
        await loadEmployees(
          roleFilter,
          empPage,
          searchQuery,
          statusFilter
        );
      } else if (activeTab === "users") {
        await loadUsers(
          usersPage,
          searchQuery,
          statusFilter
        );
      }
    };

    loadActiveTab();
  }, [
    activeTab,
    empPage,
    usersPage,
    roleFilter,
    searchQuery,
    statusFilter,
    permsLoaded,
    loadEmployees,
    loadUsers,
  ]);

  useEffect(() => {
    if (permsLoaded) {
      loadRoles();
    }
  }, [permsLoaded, loadRoles]);

  useEffect(() => {
    if (activeTab === "employees") {
      setEmpPage(1);
    }

    if (activeTab === "users") {
      setUsersPage(1);
    }

  }, [
    searchQuery,
    roleFilter,
    statusFilter,
    activeTab,
  ]);

  const handleTabChange = (
    tab:
      | "employees"
      | "users"
  ) => {
    if (tab === activeTab) {
      return;
    }

    setSearchQuery("");
    setActiveTab(tab);
  };

  const handleEmpPageChange = (
    page: number
  ) => {
    if (page === empPage) {
      return;
    }

    setEmpPage(page);
  };

  const handleUsersPageChange = (
    page: number
  ) => {
    if (page === usersPage) {
      return;
    }

    setUsersPage(page);
  };


  const clearFilters = () => {
    setSearchQuery("");
    setRoleFilter("");
    setStatusFilter("True");

    setEmpPage(1);
    setUsersPage(1);
  };

  const statusFilterOptions = [
    {
      value: "True",
      label: "Active",
    },
    {
      value: "False",
      label: "Inactive",
    },
  ];

  return (
    <div>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900">
            User & Team Management
          </h1>

          <p className="text-gray-600 mt-1">
            Manage employees, verify users, and control
            access
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 items-center justify-center">
          {canSeeEmployees && (
            <button
              onClick={() =>
                handleTabChange("employees")
              }
              className={`flex flex-row items-center justify-center gap-4 px-6 py-4 rounded-lg font-semibold transition-all ${
                activeTab === "employees"
                  ? "bg-brand-500 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <Briefcase className="w-5 h-5" />

              <span>Staff</span>

              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === "employees"
                    ? "bg-brand-500 text-white"
                    : "bg-brand-100 text-brand-700"
                }`}
              >
                {empTotalCount}
              </span>
            </button>
          )}

          {canSeeUsers && (
            <button
              onClick={() =>
                handleTabChange("users")
              }
              className={`flex flex-row items-center justify-center gap-4 px-6 py-4 rounded-lg font-semibold transition-all ${
                activeTab === "users"
                  ? "bg-brand-500 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <UsersIcon className="w-5 h-5" />

              <span>Users</span>

              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === "users"
                    ? "bg-brand-500 text-white"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {usersTotalCount}
              </span>
            </button>
          )}

         
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">
              Filters
            </h3>

            {(searchQuery ||
              roleFilter !== "" ||
              statusFilter !== "True") && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />

              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) =>
                  setSearchQuery(
                    e.target.value
                  )
                }
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              />

              {searchQuery && (
                <button
                  onClick={() =>
                    setSearchQuery("")
                  }
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Role Filter */}
            {activeTab === "employees" && (
              <select
                value={roleFilter}
                onChange={(e) =>
                  setRoleFilter(
                    e.target.value
                  )
                }
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                <option value="">
                  All Roles
                </option>

                {roles.map((role: any) => (
                  <option
                    key={role.id}
                    value={role.Name}
                  >
                    {role.Name}
                  </option>
                ))}
              </select>
            )}

            {(activeTab === "employees" ||
              activeTab === "users") && (
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value
                  )
                }
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                {statusFilterOptions.map(
                  (item) => (
                    <option
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </option>
                  )
                )}
              </select>
            )}
          </div>
        </div>

        {/* Content */}
        {activeTab === "employees" ? (
          <EmployeeList
            employees={employees}
            loading={loading}
            onRefresh={() =>
              loadEmployees(
                roleFilter,
                empPage,
                searchQuery,
                statusFilter
              )
            }
            searchQuery={searchQuery}
            statusFilter={statusFilter}
            currentPage={empPage}
            totalPages={empTotalPages}
            totalCount={empTotalCount}
            onPageChange={
              handleEmpPageChange
            }
            isBranchIncharge={
              isBranchIncharge
            }
            branchCode={branchCode}
          />
        ) : activeTab === "users" && (
          <UserList
            users={users}
            loading={loading}
            onRefresh={() =>
              loadUsers(
                usersPage,
                searchQuery,
                statusFilter
              )
            }
            searchQuery={searchQuery}
            statusFilter={statusFilter}
            currentPage={usersPage}
            totalPages={usersTotalPages}
            totalCount={usersTotalCount}
            onPageChange={
              handleUsersPageChange
            }
          />
        )}
      </div>
    </div>
  );
}
